import React, { useEffect, useState, useRef } from 'react';
import { Filter, ArrowUpDown, ArrowUp, ArrowDown, Search, X } from 'lucide-react';
import { MOCK_LEADS, Lead } from '../data/mockLeads';
import { LeadRow } from './LeadRow';
import { Button } from './Button';
import { LeadActionModal, ActionMode } from './LeadActionModal';
type PurposeFilter = 'all' | 'Purchase' | 'Refinance';
type ChannelFilter = 'all' | 'email' | 'sms' | 'mixed';
type SortKey = 'loanAmount' | 'lastContact';
type SortDir = 'asc' | 'desc';
export function LeadQueue() {
  const [leads, setLeads] = useState(MOCK_LEADS);
  const [filter, setFilter] = useState<'all' | 'pending' | 'contacted'>('all');
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [purpose, setPurpose] = useState<PurposeFilter>('all');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const filterRef = useRef<HTMLDivElement>(null);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const [activeModal, setActiveModal] = useState<{
    lead: Lead | null;
    mode: ActionMode;
  }>({
    lead: null,
    mode: 'call'
  });
  const activeFilterCount = (purpose !== 'all' ? 1 : 0) + (channel !== 'all' ? 1 : 0);
  // Close the filter dropdown when clicking outside it
  useEffect(() => {
    if (!filtersOpen) return;
    const onClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [filtersOpen]);
  const filteredLeads = leads.filter(lead => {
    if (filter !== 'all' && lead.status !== filter) return false;
    if (purpose !== 'all' && lead.loanPurpose !== purpose) return false;
    if (channel !== 'all' && lead.campaignType !== channel) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return lead.name.toLowerCase().includes(q) || lead.email.toLowerCase().includes(q) || lead.campaignName.toLowerCase().includes(q);
  });
  const sortedLeads = sortKey ? [...filteredLeads].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'loanAmount') {
      cmp = a.loanAmount - b.loanAmount;
    } else {
      cmp = new Date(a.lastContact).getTime() - new Date(b.lastContact).getTime();
    }
    return sortDir === 'asc' ? cmp : -cmp;
  }) : filteredLeads;
  const SortIcon = ({
    column
  }: {
    column: SortKey;
  }) => {
    if (sortKey !== column) return <ArrowUpDown size={14} className="text-slate-400" data-id="element-49" />;
    return sortDir === 'asc' ? <ArrowUp size={14} className="text-blue-600" data-id="element-50" /> : <ArrowDown size={14} className="text-blue-600" data-id="element-51" />;
  };
  const markContacted = (id: string) => setLeads(prev => prev.map(l => l.id === id ? {
    ...l,
    status: 'contacted',
    lastContact: new Date().toISOString()
  } : l));
  const handleAction = (id: string, action: string) => {
    if (action === 'call' || action === 'email') {
      const lead = leads.find(l => l.id === id) ?? null;
      setActiveModal({
        lead,
        mode: action as ActionMode
      });
    }
  };
  const handleModalSubmit = (leadId: string, mode: ActionMode) => {
    // Only logging a call counts as contacting the lead.
    if (mode === 'call') {
      markContacted(leadId);
    }
    setActiveModal({
      lead: null,
      mode: 'call'
    });
  };
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-52">
      <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-white" data-id="element-53">
        <div data-id="element-54">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-55">
            Today's Follow-ups
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-56">
            Leads that require your attention today.
          </p>
        </div>

        <div className="flex items-center gap-3" data-id="element-57">
          <div className="flex bg-slate-100 p-1 rounded-lg" data-id="element-58">
            <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setFilter('all')} data-id="element-59">
              All
            </button>
            <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setFilter('pending')} data-id="element-60">
              Pending
            </button>
            <button className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'contacted' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} onClick={() => setFilter('contacted')} data-id="element-61">
              Completed
            </button>
          </div>
          <div className="relative" data-id="element-62">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-63" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search leads..." aria-label="Search follow-ups" className="w-48 pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" data-id="element-64" />
          </div>
          <div className="relative" ref={filterRef} data-id="element-65">
            <Button variant={filtersOpen || activeFilterCount > 0 ? 'secondary' : 'outline'} size="sm" icon={<Filter size={16} data-id="element-67" />} onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen} aria-haspopup="true" data-id="element-66">
              Filters
              {activeFilterCount > 0 && <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded-full bg-blue-600 text-white text-xs font-semibold" data-id="element-68">
                  {activeFilterCount}
                </span>}
            </Button>

            {filtersOpen && <div role="menu" className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-20 p-4 space-y-4" data-id="element-69">
                <div className="flex items-center justify-between" data-id="element-70">
                  <h3 className="text-sm font-semibold text-slate-900" data-id="element-71">
                    Filters
                  </h3>
                  {activeFilterCount > 0 && <button type="button" onClick={() => {
                setPurpose('all');
                setChannel('all');
              }} className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-72">
                      <X size={12} data-id="element-73" /> Clear
                    </button>}
                </div>

                <div data-id="element-74">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-75">
                    Loan Purpose
                  </label>
                  <select value={purpose} onChange={e => setPurpose(e.target.value as PurposeFilter)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-76">
                    <option value="all" data-id="element-77">All purposes</option>
                    <option value="Purchase" data-id="element-78">Purchase</option>
                    <option value="Refinance" data-id="element-79">Refinance</option>
                  </select>
                </div>

                <div data-id="element-80">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-81">
                    Campaign Channel
                  </label>
                  <select value={channel} onChange={e => setChannel(e.target.value as ChannelFilter)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-82">
                    <option value="all" data-id="element-83">All channels</option>
                    <option value="email" data-id="element-84">Email Drip</option>
                    <option value="sms" data-id="element-85">SMS Drip</option>
                    <option value="mixed" data-id="element-86">Multi-channel</option>
                  </select>
                </div>
              </div>}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-87">
        <table className="w-full text-left border-collapse" data-id="element-88">
          <thead data-id="element-89">
            <tr className="bg-slate-50 border-b border-slate-200" data-id="element-90">
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-91">
                Lead Details
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" aria-sort={sortKey === 'loanAmount' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-92">
                <button type="button" onClick={() => toggleSort('loanAmount')} className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 transition-colors" data-id="element-93">
                  Loan Info <SortIcon column="loanAmount" data-id="element-94" />
                </button>
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-95">
                Current Campaign
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" aria-sort={sortKey === 'lastContact' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-96">
                <button type="button" onClick={() => toggleSort('lastContact')} className="flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 transition-colors" data-id="element-97">
                  Last Contact <SortIcon column="lastContact" data-id="element-98" />
                </button>
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-99">
                Status
              </th>
              <th className="py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right" data-id="element-100">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100" data-id="element-101">
            {sortedLeads.length > 0 ? sortedLeads.map(lead => <LeadRow key={lead.id} lead={lead} onAction={handleAction} data-id="element-102" />) : <tr data-id="element-103">
                <td colSpan={6} className="py-12 text-center text-slate-500" data-id="element-104">
                  No leads found matching the current filter.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      <LeadActionModal lead={activeModal.lead} mode={activeModal.mode} open={activeModal.lead !== null} onClose={() => setActiveModal({
      lead: null,
      mode: 'call'
    })} onSubmit={handleModalSubmit} data-id="element-105" />
    </div>;
}